import { Loader2 } from 'lucide-react';
import { lazy, Suspense } from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';

import { SkipNav, ToastProvider } from '@aia/ui-components';

import { ProtectedRoute } from '@/components/auth/ProtectedRoute';
import { AdminLayout } from '@/components/layout/AdminLayout';
import { AuthProvider } from '@/lib/auth';

const LoginPage = lazy(() =>
  import('@/features/auth/pages/LoginPage').then((m) => ({ default: m.LoginPage })),
);
const ForgotPasswordPage = lazy(() =>
  import('@/features/auth/pages/ForgotPasswordPage').then((m) => ({
    default: m.ForgotPasswordPage,
  })),
);
const ResetPasswordPage = lazy(() =>
  import('@/features/auth/pages/ResetPasswordPage').then((m) => ({ default: m.ResetPasswordPage })),
);
const SsoLandingPage = lazy(() =>
  import('@/features/auth/pages/SsoLandingPage').then((m) => ({ default: m.SsoLandingPage })),
);

const AdminDashboard = lazy(() =>
  import('@/features/dashboard/pages/AdminDashboard').then((m) => ({ default: m.AdminDashboard })),
);
const TenantsPage = lazy(() =>
  import('@/features/tenants/pages/TenantsPage').then((m) => ({
    default: m.TenantsPage,
  })),
);
const TenantDetailsPage = lazy(() =>
  import('@/features/tenants/pages/TenantDetailsPage').then((m) => ({
    default: m.TenantDetailsPage,
  })),
);
const OrganizationsPage = lazy(() =>
  import('@/features/organizations/pages/OrganizationsPage').then((m) => ({
    default: m.OrganizationsPage,
  })),
);
const OrganizationDetailsPage = lazy(() =>
  import('@/features/organizations/pages/OrganizationDetailsPage').then((m) => ({
    default: m.OrganizationDetailsPage,
  })),
);
const SubscriptionsPage = lazy(() =>
  import('@/features/subscriptions/pages/SubscriptionsPage').then((m) => ({
    default: m.SubscriptionsPage,
  })),
);
const PaymentLinksPage = lazy(() =>
  import('@/features/payments/pages/PaymentLinksPage').then((m) => ({
    default: m.PaymentLinksPage,
  })),
);
const UsersPage = lazy(() =>
  import('@/features/users/pages/UsersPage').then((m) => ({ default: m.UsersPage })),
);
const EmailTemplatesPage = lazy(() =>
  import('@/features/email-templates/pages/EmailTemplatesPage').then((m) => ({
    default: m.EmailTemplatesPage,
  })),
);
const SettingsPage = lazy(() =>
  import('@/features/settings/pages/SettingsPage').then((m) => ({ default: m.SettingsPage })),
);
const SecurityPage = lazy(() =>
  import('@/features/security/pages/SecurityPage').then((m) => ({ default: m.SecurityPage })),
);

function LoadingFallback() {
  return (
    <div className="min-h-screen flex items-center justify-center" role="status">
      <Loader2 className="h-8 w-8 animate-spin text-aia-blue" aria-hidden="true" />
      <span className="sr-only">Loading...</span>
    </div>
  );
}

export default function App() {
  return (
    <BrowserRouter>
      <SkipNav />
      <AuthProvider>
        <ToastProvider>
          <Suspense fallback={<LoadingFallback />}>
            <Routes>
              {/* Public auth routes */}
              <Route path="/login" element={<LoginPage />} />
              <Route path="/forgot-password" element={<ForgotPasswordPage />} />
              <Route path="/reset-password" element={<ResetPasswordPage />} />
              <Route path="/sso" element={<SsoLandingPage />} />

              {/* Protected admin routes */}
              <Route
                element={
                  <ProtectedRoute>
                    <AdminLayout />
                  </ProtectedRoute>
                }
              >
                <Route index element={<Navigate to="/dashboard" replace />} />
                <Route path="dashboard" element={<AdminDashboard />} />
                <Route path="tenants" element={<TenantsPage />} />
                <Route path="tenants/:id" element={<TenantDetailsPage />} />
                <Route path="organizations" element={<OrganizationsPage />} />
                <Route path="organizations/:id" element={<OrganizationDetailsPage />} />
                <Route path="subscriptions" element={<SubscriptionsPage />} />
                <Route path="payment-links" element={<PaymentLinksPage />} />
                <Route path="users" element={<UsersPage />} />
                <Route path="email-templates" element={<EmailTemplatesPage />} />
                <Route path="settings" element={<SettingsPage />} />
                <Route path="security" element={<SecurityPage />} />
              </Route>

              {/* Catch-all */}
              <Route path="*" element={<Navigate to="/dashboard" replace />} />
            </Routes>
          </Suspense>
        </ToastProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}
